import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Shield, CheckCircle, XCircle, Clock, Eye, ChevronLeft, ChevronRight,
  Loader2, Award, BookOpen, FileText, User, MapPin, Star, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  exam_passed: { label: 'Exam Passed', color: 'bg-blue-100 text-blue-800', icon: BookOpen },
};

export default function AdminPanditVerificationPage({ admin }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [decisionType, setDecisionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const token = localStorage.getItem('dharmbook_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/pandit-verification/requests`, {
        headers,
        params: { status: statusFilter },
      });
      setRequests(res.data.requests || []);
    } catch (err) {
      toast.error('Failed to load verification requests');
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async () => {
    if (!selectedRequest) return;
    if (decisionType === 'reject' && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/pandit-verification/requests/${selectedRequest.id}/review`,
        { decision: decisionType, reason: rejectionReason },
        { headers }
      );
      toast.success(`Verification ${decisionType === 'approve' ? 'approved' : 'rejected'} successfully`);
      setShowDecisionModal(false);
      setShowDetailModal(false);
      setRejectionReason('');
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process decision');
    } finally {
      setActionLoading(false);
    }
  };

  const openDecisionModal = (type, request) => {
    setSelectedRequest(request);
    setDecisionType(type);
    setRejectionReason('');
    setShowDecisionModal(true);
  };

  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
  };

  const getStatusInfo = (status) => STATUS_STYLES[status] || STATUS_STYLES.pending_review;

  return (
    <div className="space-y-6" data-testid="admin-pandit-verification">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Pandit Exam Verification</h1>
          <p className="text-slate-500 text-sm mt-1">Review and approve pandit certification requests</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm px-3 py-1">
            {requests.length} requests
          </Badge>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-1" data-testid="status-filter-tabs">
        {[
          { value: 'pending_review', label: 'Pending', count: null },
          { value: 'exam_passed', label: 'Exam Passed' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'all', label: 'All' },
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              statusFilter === tab.value
                ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            data-testid={`filter-${tab.value}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">No verification requests</p>
          <p className="text-sm mt-1">No {statusFilter === 'all' ? '' : statusFilter.replace('_', ' ')} requests found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full" data-testid="verification-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Pandit</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Exam Score</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Documents</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Submitted</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => {
                const si = getStatusInfo(req.status);
                const StatusIcon = si.icon;
                return (
                  <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`verification-row-${req.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {req.user_avatar ? (
                            <img src={req.user_avatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-5 h-5 text-orange-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 text-sm">{req.user_name || 'Unknown'}</p>
                          <p className="text-xs text-slate-400">{req.user_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {req.exam_score !== null ? (
                        <div className="flex items-center gap-1.5">
                          <Award className={`w-4 h-4 ${req.exam_score >= 70 ? 'text-green-500' : 'text-red-400'}`} />
                          <span className={`font-bold text-sm ${req.exam_score >= 70 ? 'text-green-700' : 'text-red-600'}`}>
                            {req.exam_score}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {req.documents_uploaded ? (
                        <div className="flex items-center gap-1.5">
                          <FileText className="w-4 h-4 text-blue-500" />
                          <span className="text-xs text-slate-600">{req.id_proof_type?.toUpperCase()}: {req.id_proof_number}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Not submitted</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${si.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {si.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-500">
                        {req.updated_at ? new Date(req.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDetailModal(req)}
                          data-testid={`view-details-${req.id}`}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                        {req.status === 'pending_review' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => openDecisionModal('approve', req)}
                              data-testid={`approve-btn-${req.id}`}
                            >
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => openDecisionModal('reject', req)}
                              data-testid={`reject-btn-${req.id}`}
                            >
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-500" />
              Verification Details
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4" data-testid="verification-detail-modal">
              {/* Pandit Info */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden">
                  {selectedRequest.user_avatar ? (
                    <img src={selectedRequest.user_avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-6 h-6 text-orange-600" />
                  )}
                </div>
                <div>
                  <p className="font-bold text-slate-800">{selectedRequest.user_name}</p>
                  <p className="text-xs text-slate-500">{selectedRequest.user_email}</p>
                  {selectedRequest.profile_city && (
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" /> {selectedRequest.profile_city}
                    </p>
                  )}
                </div>
              </div>

              {/* Exam Results */}
              <div className="border border-slate-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-orange-500" /> Exam Results
                </h3>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-600">Overall Score</span>
                  <span className={`text-lg font-bold ${selectedRequest.exam_score >= 70 ? 'text-green-600' : 'text-red-500'}`}>
                    {selectedRequest.exam_score ?? '—'}%
                  </span>
                </div>
                {selectedRequest.exam_topic_scores && (
                  <div className="space-y-2">
                    {Object.entries(selectedRequest.exam_topic_scores).map(([topic, scores]) => {
                      const pct = Math.round((scores.correct / scores.total) * 100);
                      return (
                        <div key={topic} className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 w-20 flex-shrink-0">{topic}</span>
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${pct >= 70 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-slate-600 w-10 text-right">{scores.correct}/{scores.total}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Documents */}
              <div className="border border-slate-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" /> Documents
                </h3>
                {selectedRequest.documents_uploaded ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">ID Proof Type</span>
                      <span className="font-medium text-slate-700">{selectedRequest.id_proof_type?.toUpperCase()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">ID Number</span>
                      <span className="font-medium text-slate-700">{selectedRequest.id_proof_number}</span>
                    </div>
                    {selectedRequest.certificate_info && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Certificates</span>
                        <span className="font-medium text-slate-700">{selectedRequest.certificate_info}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Documents not yet submitted</p>
                )}
              </div>

              {/* Profile Info */}
              {(selectedRequest.profile_experience || selectedRequest.profile_specialization) && (
                <div className="border border-slate-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-green-500" /> Profile
                  </h3>
                  <div className="space-y-2 text-sm">
                    {selectedRequest.profile_experience > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Experience</span>
                        <span className="font-medium text-slate-700">{selectedRequest.profile_experience} years</span>
                      </div>
                    )}
                    {selectedRequest.profile_specialization && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Specialization</span>
                        <span className="font-medium text-slate-700">{selectedRequest.profile_specialization}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Rejection Reason */}
              {selectedRequest.status === 'rejected' && selectedRequest.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-xs font-semibold text-red-700 mb-1">Rejection Reason</p>
                  <p className="text-sm text-red-600">{selectedRequest.rejection_reason}</p>
                </div>
              )}

              {/* Action Buttons */}
              {selectedRequest.status === 'pending_review' && (
                <div className="flex gap-3 pt-2">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => openDecisionModal('approve', selectedRequest)}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Approve
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => openDecisionModal('reject', selectedRequest)}
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Decision Confirmation Modal */}
      <Dialog open={showDecisionModal} onOpenChange={setShowDecisionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {decisionType === 'approve' ? (
                <><CheckCircle className="w-5 h-5 text-green-500" /> Approve Verification</>
              ) : (
                <><XCircle className="w-5 h-5 text-red-500" /> Reject Verification</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="decision-modal">
            {decisionType === 'approve' ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm text-green-700">
                  This will mark <strong>{selectedRequest?.user_name}</strong> as a DharmBook Certified Pandit.
                  They will receive the verified badge on their profile.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm text-red-700">
                    Please provide a reason for rejecting <strong>{selectedRequest?.user_name}</strong>'s verification request.
                  </p>
                </div>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Reason for rejection..."
                  className="min-h-[80px]"
                  data-testid="rejection-reason-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionModal(false)}>
              Cancel
            </Button>
            <Button
              className={decisionType === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
              variant={decisionType === 'reject' ? 'destructive' : 'default'}
              onClick={handleDecision}
              disabled={actionLoading}
              data-testid="confirm-decision-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {decisionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
